import React, { useState } from 'react';
import { RefreshCw, Copy, Check } from 'lucide-react';
import { generateStrongPassword } from '../../lib/auth/passwordSecurity';
import { toast } from 'react-hot-toast';

interface PasswordGeneratorProps {
  onSelect: (password: string) => void;
  className?: string;
}

export function PasswordGenerator({ onSelect, className }: PasswordGeneratorProps) {
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const newPassword = generateStrongPassword();
    setGeneratedPassword(newPassword);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const handleSelect = () => {
    onSelect(generatedPassword);
    toast.success('Password selected');
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="flex-1">
          <input
            type="text"
            value={generatedPassword}
            readOnly
            className="w-full bg-transparent text-sm font-mono focus:outline-none"
            placeholder="Click generate to create a strong password"
          />
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleGenerate}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            title="Generate new password"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={handleCopy}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      {generatedPassword && (
        <button
          onClick={handleSelect}
          className="mt-2 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Use This Password
        </button>
      )}
    </div>
  );
}